import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useBulkRemoveGroupUsers, useGroupsList, useUsersInGroup } from '../api';
import type { UserInGroupRow } from '../types';

export function BulkUpdateGroupPage() {
  const groups = useGroupsList();
  const [groupId, setGroupId] = useState<string>('');
  const members = useUsersInGroup(groupId || null);
  const remove = useBulkRemoveGroupUsers();

  const [doSwap, setDoSwap] = useState(false);
  const [swapGroupId, setSwapGroupId] = useState<string>('');
  const [pending, setPending] = useState<{ ids: string[]; clear: () => void } | null>(null);

  const columns = useMemo<ColumnDef<UserInGroupRow>[]>(
    () => [
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId}
          </span>
        ),
      },
      { accessorKey: 'username', header: 'Username' },
      {
        accessorKey: 'groupName',
        header: 'Group',
        cell: ({ row }) => row.original.groupName ?? '—',
      },
    ],
    [],
  );

  const groupLabel =
    (groups.data ?? []).find((g) => String(g.groupId) === groupId)?.groupName ?? '';
  const swapLabel =
    (groups.data ?? []).find((g) => String(g.groupId) === swapGroupId)?.groupName ?? '';

  const performRemove = () => {
    if (!pending) return;
    if (doSwap && !swapGroupId) {
      toast.error('Pick a group to swap to, or untick the swap option.');
      return;
    }
    remove.mutate(
      {
        groupId,
        userIds: pending.ids.join(','),
        swapGroupId: doSwap ? swapGroupId : undefined,
      },
      {
        onSuccess: (res) => {
          toast.success(
            res?.message ??
              (doSwap
                ? `Swapped ${pending.ids.length} users to ${swapLabel}`
                : `Removed ${pending.ids.length} users from ${groupLabel}`),
          );
          pending.clear();
          setPending(null);
        },
        onError: (err) => toast.error(readErrorMessage(err) ?? 'Bulk remove failed.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Bulk Remove Users from Group"
        description="Detach many users from a group at once, optionally moving them to another group."
        actions={
          <Button asChild variant="outline">
            <Link to="/group-list">
              <ArrowLeft className="h-4 w-4" />
              Back to groups
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Source group</CardTitle>
          <CardDescription>
            Showing users currently inside this group.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-md">
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger>
                <SelectValue
                  placeholder={groups.isLoading ? 'Loading groups…' : 'Select a group'}
                />
              </SelectTrigger>
              <SelectContent>
                {(groups.data ?? []).map((g) => (
                  <SelectItem key={g.groupId} value={String(g.groupId)}>
                    {g.groupName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2">
              <Checkbox
                id="do-swap"
                checked={doSwap}
                onCheckedChange={(v) => {
                  setDoSwap(Boolean(v));
                  if (!v) setSwapGroupId('');
                }}
              />
              <Label htmlFor="do-swap" className="cursor-pointer">
                Swap users into another group instead of removing
              </Label>
            </div>
            {doSwap && (
              <div className="max-w-md flex-1">
                <Select value={swapGroupId} onValueChange={setSwapGroupId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a target group to swap into" />
                  </SelectTrigger>
                  <SelectContent>
                    {(groups.data ?? [])
                      .filter((g) => String(g.groupId) !== groupId)
                      .map((g) => (
                        <SelectItem key={g.groupId} value={String(g.groupId)}>
                          {g.groupName}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <DataTable<UserInGroupRow, unknown>
        tableId="bulk:remove-group:v1"
        columns={columns}
        data={members.data ?? []}
        isLoading={members.isFetching}
        searchPlaceholder="Search members…"
        emptyTitle={groupId ? 'No users in this group' : 'Pick a group to begin'}
        emptyDescription={
          groupId
            ? 'This group has no users to remove.'
            : 'Choose a source group from the dropdown above.'
        }
        getRowId={(row) => String(row.userId)}
        enableRowSelection
        exportFilename="group-members.csv"
        pageSize={25}
        renderSelectionActions={(ids, clear) => (
          <Button
            size="sm"
            variant={doSwap ? 'default' : 'destructive'}
            onClick={() => {
              if (doSwap && !swapGroupId) {
                toast.error('Pick a group to swap to.');
                return;
              }
              setPending({ ids, clear });
            }}
            disabled={ids.length === 0 || remove.isPending}
          >
            <Trash2 className="h-4 w-4" />
            {doSwap ? `Swap ${ids.length} to ${swapLabel || 'group'}` : `Remove ${ids.length}`}
          </Button>
        )}
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title="Are you sure you want to delete?"
        description={
          doSwap ? (
            <>
              Move <span className="font-medium text-foreground">{pending?.ids.length ?? 0}</span>{' '}
              user{pending?.ids.length === 1 ? '' : 's'} from{' '}
              <span className="font-medium text-foreground">{groupLabel}</span> to{' '}
              <span className="font-medium text-foreground">{swapLabel}</span>?
            </>
          ) : (
            <>
              Remove <span className="font-medium text-foreground">{pending?.ids.length ?? 0}</span>{' '}
              user{pending?.ids.length === 1 ? '' : 's'} from{' '}
              <span className="font-medium text-foreground">{groupLabel}</span>?
            </>
          )
        }
        confirmLabel={doSwap ? 'Swap users' : 'Remove'}
        tone={doSwap ? 'default' : 'destructive'}
        loading={remove.isPending}
        onConfirm={performRemove}
      />
    </FillPage>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
